// Shared tone definitions and copy generators for outgoing customer emails.
// Three Laws compliance: no accusatory language, transparency in body,
// ARTINT suggests proactively. Keep opener strings synced with the edge
// function template (we pass the rendered text in, so this file is the
// single source of truth).

export type EmailTone = "friendly" | "standard" | "firm";

export const TONES: { value: EmailTone; label: string; blurb: string }[] = [
  { value: "friendly", label: "Friendly", blurb: "Warm, first-name feel" },
  { value: "standard", label: "Standard", blurb: "Professional, neutral" },
  { value: "firm", label: "Firm", blurb: "Direct, clear urgency" },
];

export function fmtCurrency(n: number | null | undefined): string {
  const x = Number(n ?? 0);
  return x.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function fmtDate(d: string | null | undefined): string {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "";
  return `${dt.getMonth() + 1}/${dt.getDate()}/${dt.getFullYear()}`;
}

/**
 * Full brand name for customer-facing subject lines. Never send the internal
 * brand abbreviation ("CD"/"SR") to a customer.
 */
export function brandDisplayName(brand: string | null | undefined): string {
  const key = String(brand ?? "").trim().toUpperCase();
  if (key === "CD") return "The Computer Doctors";
  if (key === "SR") return "SummitRidge Technology";
  return "Your Service Provider";
}

export interface OpenerCtx {
  invoiceNumber: string;
  companyName: string;
  contactFirstName?: string | null;
  total: number;
  dueDate?: string | null;
  sentDate?: string | null;
  brandName: string;
}

export function buildSendInvoiceOpener(tone: EmailTone, ctx: OpenerCtx): string {
  const total = `$${fmtCurrency(ctx.total)}`;
  const due = ctx.dueDate ? fmtDate(ctx.dueDate) : "the due date";
  const greet = ctx.contactFirstName?.trim() || "there";
  switch (tone) {
    case "friendly":
      return `Hi ${greet}, please find Invoice ${ctx.invoiceNumber} attached below. We appreciate your business and look forward to hearing from you!`;
    case "firm":
      return `Invoice ${ctx.invoiceNumber} for ${ctx.companyName} is enclosed. Payment of ${total} is due by ${due}. Please remit payment at your earliest convenience.`;
    case "standard":
    default:
      return `Please find Invoice ${ctx.invoiceNumber} for ${ctx.companyName} enclosed. Payment is due by ${due}.`;
  }
}

export function buildSendInvoiceSubject(tone: EmailTone, ctx: OpenerCtx): string {
  const total = `$${fmtCurrency(ctx.total)}`;
  switch (tone) {
    case "friendly":
      return `Invoice ${ctx.invoiceNumber} from ${ctx.brandName}`;
    case "firm":
      return `Invoice ${ctx.invoiceNumber} — Payment Due ${ctx.dueDate ? fmtDate(ctx.dueDate) : ""}`.trim();
    case "standard":
    default:
      return `Invoice ${ctx.invoiceNumber} — ${ctx.companyName} · ${total}`;
  }
}

export function buildPaymentRequestOpener(tone: EmailTone, ctx: OpenerCtx): string {
  const total = `$${fmtCurrency(ctx.total)}`;
  const sent = ctx.sentDate ? fmtDate(ctx.sentDate) : "recently";
  const greet = ctx.contactFirstName?.trim() || "there";
  switch (tone) {
    case "friendly":
      return `Hi ${greet}, this is a friendly reminder that Invoice ${ctx.invoiceNumber} sent on ${sent} remains unpaid. We'd love to get this sorted for you — please use the button below whenever convenient!`;
    case "firm":
      return `Payment of ${total} on Invoice ${ctx.invoiceNumber} (sent ${sent}) is outstanding. Please remit payment immediately using the link below.`;
    case "standard":
    default:
      return `This is a reminder that Invoice ${ctx.invoiceNumber} for ${total}, sent on ${sent}, remains outstanding. Please click below to pay.`;
  }
}

export function buildPaymentRequestSubject(tone: EmailTone, ctx: OpenerCtx): string {
  const total = `$${fmtCurrency(ctx.total)}`;
  switch (tone) {
    case "friendly":
      return `Friendly reminder — Invoice ${ctx.invoiceNumber} · ${total}`;
    case "firm":
      return `Action Required — Invoice ${ctx.invoiceNumber} · ${total} Outstanding`;
    case "standard":
    default:
      return `Payment Due — Invoice ${ctx.invoiceNumber} · ${total}`;
  }
}
